import { useConfig } from '@/composables/game/config'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_PLACEMENT,
  ECOMMERCE_ITEM_TAG,
  type EcommerceItem,
  SALE_DISCOUNT_TYPE,
} from '@/types/shared/player-config'
import { computed, type ComputedRef, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import { useNow } from '@/composables/game/time'
import { useEcommerceItems, useEcommercePrice } from '@/composables/game/ecommerce'
import { DISCOUNT_EVENT } from '@/types/shop'
import { usePlayerSegmenter } from '@/composables/game/player-segmenter'

export const useShopHelper = () => {
  const { player } = usePlayer()
  const { now } = useNow()
  const lastPurchases = computed(() => player.value.ecommerce.lastPurchases)

  function getItemDisabledDuration(item: EcommerceItem) {
    if (!item.limits.time || item.limits.time.type !== 'afterPurchase') return 0
    return item.limits.time.duration
  }

  function getItemLastPurchasedAt(id: string) {
    return lastPurchases.value[id]?.purchasedAt ?? 0
  }

  function getItemDisabledSeconds(item: EcommerceItem) {
    const itemDisabledDuration = getItemDisabledDuration(item)
    if (!itemDisabledDuration) return 0
    const elapsed = now.value - getItemLastPurchasedAt(item.id)
    return Math.max(itemDisabledDuration - elapsed, 0)
  }

  return {
    getItemDisabledDuration,
    getItemLastPurchasedAt,
    getItemDisabledSeconds,
  }
}

export const useShop = () => {
  const { ecommerceItems } = useEcommerceItems()
  const { config, syncConfig } = useConfig()
  const { now } = useNow()
  const { getItemDisabledSeconds } = useShopHelper()
  const { segment } = usePlayerSegmenter()

  const sales = computed(() => {
    const maybeSale = config.value.game.ecommerce.sales
    if (!segment(maybeSale?.segmentation)) {
      return null
    }
    return maybeSale
  })
  const saleStarts = computed(() => sales.value?.start)
  const saleEnds = computed(() => sales.value?.end)
  const saleType = computed(() => sales.value?.discount.type)
  const saleEvent = computed(() => sales.value?.theme)
  const saleName = computed(() => sales.value?.eventName)
  const saleValue = computed(() => (sales.value?.discount as {
    type: SALE_DISCOUNT_TYPE.DISCOUNT | SALE_DISCOUNT_TYPE.CASHBACK | SALE_DISCOUNT_TYPE.BUY_ONE_GET_X,
    amount: number
  })?.amount ?? 0)

  const countdownForSaleStart = computed(() => {
    if (!saleStarts.value) return 0
    const timeDiff = saleStarts.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })
  const countdownForSaleEnds = computed(() => {
    if (!saleEnds.value) return 0
    const timeDiff = saleEnds.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const isSale = computed(() => (countdownForSaleStart.value <= 0) && (countdownForSaleEnds.value > 0))
  const isDefault = computed(() => saleEvent.value === DISCOUNT_EVENT.DEFAULT)

  const isDiscount = computed(() => isSale.value && saleType.value === SALE_DISCOUNT_TYPE.DISCOUNT)
  const isDefaultDiscount = computed(() => isDiscount.value && isDefault.value)
  // Themed discount sales
  const isIWD = computed(() => isDiscount.value && saleEvent.value === DISCOUNT_EVENT.IWD)
  const isSTP = computed(() => isDiscount.value && saleEvent.value === DISCOUNT_EVENT.STP)

  const isBOGO = computed(() => isSale.value && saleType.value === SALE_DISCOUNT_TYPE.BUY_ONE_GET_TWO)
  const isDefaultBOGO = computed(() => isBOGO.value && isDefault.value)

  const isBOGX = computed(() => isSale.value && saleType.value === SALE_DISCOUNT_TYPE.BUY_ONE_GET_X)
  const isDefaultBOGX = computed(() => isBOGX.value && isDefault.value)

  const isCashback = computed(() => isSale.value && saleType.value === SALE_DISCOUNT_TYPE.CASHBACK)
  const isDefaultCashback = computed(() => isCashback.value && isDefault.value)

  const shopItems = computed(() => ecommerceItems.value.filter((item) => item.tags.includes(ECOMMERCE_ITEM_TAG.SHOP_ITEM)))

  const canHaveSale = computed(() => shopItems.value.some((item) =>
    item.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT) &&
    item.tags.some(tag =>
      Object.entries(ECOMMERCE_ITEM_TAG).some(([key, value]) => /^GROUP_.*/.test(key) && tag === value),
    ) &&
    !item.archivedAt))

  async function syncShop() {
    await syncConfig()
  }

  watch(countdownForSaleEnds, async () => {
    if (countdownForSaleEnds.value <= 0) {
      await syncShop()
    }
  })

  return {
    shopItems,
    getItemDisabledSeconds,
    isSale,
    isDiscount,
    discountType: saleType,
    isDefaultBOGO,
    isIWD,
    isSTP,
    isBOGO,
    isCashback,
    isDefaultCashback,
    isDefaultDiscount,
    saleValue,
    countdownForSaleStart,
    countdownForSaleEnds,
    syncShop,
    saleName,
    canHaveSale,
    isBOGX,
    isDefaultBOGX,
  }
}

export const useShopItem = (shopItem: ComputedRef<EcommerceItem>) => {
  const { config } = useConfig()
  const { getStarPrice } = useEcommercePrice()

  const itemStarPrice = computed(() => getStarPrice(shopItem.value))
  const spinBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN)?.amount ?? 0)
  const starBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.STAR)?.amount ?? 0)
  const goldenCoinBenefit = computed<number>(() => (shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN)?.amount ?? 0) * config.value.game.ecommerce.currencies.goldenCoin)
  const coinBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COIN)?.amount ?? 0)
  const offlineProfitBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT)?.amount ?? 0)
  const tradingPerHourBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2)?.amount ?? 0)
  const virtualFriendBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.VIRTUAL_FRIEND)?.amount ?? 0)
  const specialCurrencyBenefit = computed<number>(() => shopItem.value.benefits.find((benefit) => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY)?.amount ?? 0)
  const hasSale = computed(() => shopItem.value.tags.includes(ECOMMERCE_ITEM_TAG.CAN_HAVE_DISCOUNT))
  return {
    itemStarPrice,
    spinBenefit,
    starBenefit,
    goldenCoinBenefit,
    coinBenefit,
    offlineProfitBenefit,
    tradingPerHourBenefit,
    virtualFriendBenefit,
    specialCurrencyBenefit,
    hasSale,
  }
}

export const useRunningOffers = () => {
  const { player } = usePlayer()
  const { now } = useNow()

  const runningOffers = computed(() => player.value.ecommerce.runningOffers ?? {})
  const lastPurchases = computed(() => player.value.ecommerce.lastPurchases)

  const isRunningOfferAvailable = (item: EcommerceItem): boolean => {
    const instanceDuration = item.config.instanceDuration
    if (!instanceDuration) return true

    const runningOffer = runningOffers.value[item.id]
    if (!runningOffer) return false

    const maxPurchaseCount = item.config.maxPurchaseCount ?? Infinity
    const instancePurchaseCount = runningOffer.instancePurchaseCount ?? 0
    if (instancePurchaseCount >= maxPurchaseCount) return false

    return now.value < runningOffer.startTimestamp + instanceDuration
  }

  const getInstanceEndTimestamp = (item: EcommerceItem): number | null => {
    const instanceDuration = item.config.instanceDuration
    if (!instanceDuration) return null

    const runningOffer = runningOffers.value[item.id]
    if (!runningOffer) return null

    return runningOffer.startTimestamp + instanceDuration
  }

  const getCycleEndTimestamp = (item: EcommerceItem): number | null => {
    const instanceDuration = item.config.instanceDuration
    if (!instanceDuration) return null

    const runningOffer = runningOffers.value[item.id]
    if (!runningOffer) return null

    const cooldown = item.config.cooldown ?? 0
    return runningOffer.startTimestamp + instanceDuration + cooldown
  }

  return {
    runningOffers,
    lastPurchases,
    isRunningOfferAvailable,
    getInstanceEndTimestamp,
    getCycleEndTimestamp,
  }
}

export type FrontendLimitedPack = EcommerceItem & { hidden: boolean }

export const usePacks = (placement?: ECOMMERCE_ITEM_PLACEMENT, watchCountdown?: boolean) => {
  const { ecommerceItems } = useEcommerceItems()
  const { now } = useNow()
  const { isRunningOfferAvailable, getCycleEndTimestamp } = useRunningOffers()

  const seenLimitedPackIds = useLocalStorage<string[]>(buildDropeeStorageKey('seenLimitedPackIds'), [])
  const limitedPackItems = computed<FrontendLimitedPack[]>(() => {
    return ecommerceItems.value
      .filter((item) =>
        item.tags.includes(ECOMMERCE_ITEM_TAG.LIMITED_TIME) &&
        item.canPurchase &&
        item.start && item.start <= now.value &&
        item.end && item.end > now.value &&
        (!placement || !item.config.placement || item.config.placement.includes(placement)),
      )
      .map((item) => ({
        ...item,
        hidden: !isRunningOfferAvailable(item),
      }))
  })

  const activeLimitedPackItems = computed<FrontendLimitedPack[]>(() =>
    limitedPackItems.value.filter((item) => !item.hidden),
  )

  const closestExpiringPack = computed<FrontendLimitedPack | undefined>(() => {
    if (!activeLimitedPackItems.value.length) return undefined
    return [...activeLimitedPackItems.value].sort((a, b) => (a.end ?? 0) - (b.end ?? 0))[0]
  })

  const hasUnseenLimitedPacks = computed(() => {
    return activeLimitedPackItems.value.some((item) => !seenLimitedPackIds.value.includes(item.id))
  })

  function markLimitedPacksAsSeen() {
    seenLimitedPackIds.value = activeLimitedPackItems.value.map((item) => item.id)
  }

  const allLimitedTimeItems = computed(() => {
    return ecommerceItems.value.filter((item) =>
      item.tags.includes(ECOMMERCE_ITEM_TAG.LIMITED_TIME) &&
      item.start && item.start <= now.value &&
      item.end && item.end > now.value &&
      (!placement || !item.config.placement || item.config.placement.includes(placement)),
    )
  })

  const nextCycleEndTimestamp = computed(() => {
    return allLimitedTimeItems.value.reduce<number | null>((min, item) => {
      const ts = getCycleEndTimestamp(item)
      if (!ts || ts <= now.value) return min
      return min === null ? ts : Math.min(min, ts)
    }, null)
  })

  const countdownForNextCycleEnd = computed(() => {
    return nextCycleEndTimestamp.value ? Math.max(nextCycleEndTimestamp.value - now.value, 0) : 0
  })

  if (watchCountdown) {
    watch(countdownForNextCycleEnd, async (countdown) => {
      if (countdown === 0) {
        setTimeout(async () => {
          await syncPlayer(true)
        }, 1000)
      }
    })
  }

  return {
    limitedPackItems,
    activeLimitedPackItems,
    closestExpiringPack,
    hasUnseenLimitedPacks,
    markLimitedPacksAsSeen,
  }
}

export const usePack = (limitedPack: ComputedRef<EcommerceItem | undefined>) => {
  const { now } = useNow()
  const { getInstanceEndTimestamp, runningOffers, lastPurchases } = useRunningOffers()

  const countdownForLimitedPackEnd = computed(() => {
    if (!limitedPack.value?.start || !limitedPack.value.end) return 0

    const instanceEnd = getInstanceEndTimestamp(limitedPack.value)
    const campaignEnd = limitedPack.value.end
    const endTimestamp = instanceEnd ? Math.min(instanceEnd, campaignEnd) : campaignEnd

    return Math.max(endTimestamp - now.value, 0)
  })

  const remainingPurchaseCount = computed(() => {
    if (!limitedPack.value) return null
    const maxCount = limitedPack.value.config.maxPurchaseCount
    if (!maxCount) return null
    const purchasedCount = limitedPack.value.config.instanceDuration
      ? (runningOffers.value[limitedPack.value.id]?.instancePurchaseCount ?? 0)
      : (lastPurchases.value[limitedPack.value.id]?.count ?? 0)
    return maxCount - purchasedCount
  })

  return {
    countdownForLimitedPackEnd,
    remainingPurchaseCount,
  }
}
